/**
 * DKM-5193 Cedric: Ekran hata kaydı (Helpdesk Faz 1).
 *
 * Oturum boyunca alınan son 50 servis/ekran hatasını tarayıcı belleğinde tutan halka tampon.
 * Beslemeler: AuthInterceptor (HttpClient), DataCoreService onAjaxError (OData/AspNetData — interceptor'dan geçmez),
 * UtilsService.showError (kullanıcının gördüğü mesaj). Okuyanlar: list-footer / form-footer "Hata Mesajları" düğmesi,
 * Faz 2'de destek talebi formu.
 *
 * GÜVENCE — bu dosya hiçbir hata akışını değiştirmez:
 *  - Dışarıya açık her yöntem kendi içinde try/catch'lidir; hiçbir koşulda istisna fırlatmaz, değer döndürmesi
 *    beklenmez (çağıranın akışı aynen sürer). Tek istisna `kaydetmeden(fn)`: fn'in kendi davranışını (dönüş
 *    değeri/istisnası) aynen geçirir, yalnız o süre kayıt yazımını susturur.
 *  - Aldığı nesneleri (HttpErrorResponse, xhr, onAjaxError olayı) değiştirmez; yalnız okur.
 *  - Angular/DI bağımlılığı ve import'u yoktur (libs/auth ↔ libs/ui döngüsü oluşmaz, testlerde `new` ile
 *    kurulan bileşenlerin kurucusu değişmez).
 *
 * KVKK — maskeleme: Authorization/Bearer/JWT, URL ve JSON'daki token/şifre alanları, 10–11 haneli sayılar
 * (TCKN/VKN), e-posta ve telefon `***` olur; istek gövdesi hiç saklanmaz; mesaj 500 karakterle kırpılır.
 * Kayıtlar yalnız bellekte durur (sayfa yenilenince silinir), sunucuya gönderilmez.
 */

export type EkranHataKaynak = 'http' | 'odata' | 'ekran';

export interface EkranHataKaydi {
  /** Oturum içi sıra no (1'den artar). */
  no: number;
  zaman: Date;
  /** Hatanın oluştuğu andaki etkin sekme (dabnet-kamu sekme yolu) ya da sayfa adresi. */
  sekme: string;
  kaynak: EkranHataKaynak;
  /** HTTP yöntemi (http kaynağında); OData'da işlem bilinmiyorsa boş. */
  yontem?: string;
  /** Maskeli adres. */
  url?: string;
  /** HTTP durum kodu (0 = sunucuya ulaşılamadı). */
  durum?: number;
  sureMs?: number;
  /** Servisten çözülen (maskeli) hata metni. */
  mesaj: string;
  /** Aynı hata kullanıcıya showError ile gösterildiyse gösterilen başlık + metin (maskeli). */
  baslik?: string;
  gosterilen?: string;
  /**
   * dtokgoz 16.10: hatanın kaynağı (ekranda gösterilmez; destek talebi ekine/dahili nota girer).
   * Kullanıcının hatadan hemen önce tıkladığı düğme/menü/sekme etiketi (maskeli).
   */
  islem?: string;
  /** 'ekran' kaydında: aynı sekmede hatadan hemen önce başlayan son servis isteği ("POST <maskeli adres>") — 200 dönüp gövdesinde hata taşıyan servisler (ApiResult) için. */
  sonIstek?: string;
  /** showError'ı çağıran metotlar (yığından, en yakın önce; ör. "GenKisiFormComponent.kpsGuncelle"). */
  cagiran?: string[];
  /** dtokgoz 17.10: hata anında o sekmede açık/seçili kayıt (footer'ların recordData'sından; detay sekmesinde başlık da). */
  kayit?: EkranHataKayitBilgisi;
}

/** dtokgoz 17.10: hatanın alındığı kayıt — PK (Kod) + birkaç iş anahtarı (sicil/ihale/fiş no…), maskeli. */
export interface EkranHataKayitBilgisi {
  /** Footer'ın formName/formCode'u (son parça, ör. 'GenKisiForm'). */
  form?: string;
  kod?: string;
  /** Kayıttaki iş anahtarları (en çok 3; TC/vergi/IBAN/telefon gibi alanlar hiç alınmaz). */
  alanlar?: { [ad: string]: string };
  /** Kayıt detayı sekmesinin başlığı (ör. "Kişi Detay - TURAN DUMAN"). */
  baslik?: string;
}

export interface EkranHataHttpGirdi {
  yontem?: string;
  url?: string | null;
  durum?: number;
  durumMetni?: string;
  govde?: unknown;
  sureMs?: number;
}

const KAPASITE = 50;
const MESAJ_SINIR = 500;
/** showError, http/odata kaydından bu süre içinde gelirse aynı hata sayılır, ikinci satır açılmaz. */
const BIRLESTIRME_MS = 2000;

const MASKE = '***';

/** dtokgoz 16.10: hatadan bu kadar önceki tıklama / istek hatanın kaynağı sayılır. */
const ISLEM_MS = 30000;
const ISTEK_MS = 30000;
const ETIKET_SINIR = 80;
/** Tıklamanın "işlem" sayıldığı öğeler (düğme, menü, sekme, bağlantı, liste öğesi). */
const TIKLAMA_SECICI =
  'button,.dx-button,[role="button"],a[href],.dx-menu-item,.dx-tab,.dx-treeview-item,.dx-list-item,[role="menuitem"],[role="tab"]';
/** Yığında atlanan çerçeveler: genel geri çağrı adları, rxjs/zone/Angular iç adları. */
const YIGIN_ATLA =
  /^(?:Object\.)?(?:next|error|complete|_next|_error|_complete|_throw|subscribe|_trySubscribe|handler|fulfilled|rejected|step|invoke|invokeTask|runTask|run|onInvoke|onInvokeTask|eval|asyncGeneratorStep|_asyncToGenerator|__awaiter|timer|<anonymous>)$|^(?:Generator|Array|Promise|new Promise)\b|Subscriber|Observer|Zone|zone|rxjs|Subscription|Observable|Subject|Operator|executeListener|wrapListener|callHook|refreshView|detectChanges|Notification|drainMicroTaskQueue|AsyncStackTagging/;
const YIGIN_AZAMI = 3;

/** dtokgoz 17.10: kaydı tanıtan alanlar (öncelik sırasıyla, büyük/küçük harf duyarsız). */
const KAYIT_ANAHTAR = [
  'SicilKod', 'KurumSicilNo', 'SicilNo', 'IhaleNo', 'DosyaNo', 'FisNo', 'BelgeNo', 'EvrakNo', 'SozlesmeNo', 'AboneNo',
  'BeyanNo', 'RuhsatNo', 'TahakkukNo', 'TalepNo', 'BasvuruNo', 'KararNo', 'Yil',
];
/** Kayıt bilgisine hiç girmeyen (kişisel/finansal) alan adları. */
const KAYIT_HARIC = /tc|kimlik|vergi|iban|tel|gsm|mail|posta|ehliyet|ssk|gss|cuzdan|sandik|kefalet|tasarruf|hesap|police|pasaport|kart|kapi|daire|blok|sifre|parola|token/i;
const KAYIT_ALAN_AZAMI = 3;
const KAYIT_DEGER_SINIR = 40;

const HASSAS_ANAHTAR =
  'access_token|id_token|refresh_token|token|code|password|passwd|pwd|sifre|şifre|parola|secret|client_secret|apikey|api_key|anahtar|authorization';

const JWT_RE = /eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]*/g;
const BEARER_RE = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi;
const URL_PARAM_RE = new RegExp('([?&;#](?:' + HASSAS_ANAHTAR + ')=)[^&#\\s"]*', 'gi');
const JSON_ALAN_RE = new RegExp('("(?:' + HASSAS_ANAHTAR + ')"\\s*:\\s*)"[^"]*"', 'gi');
const EPOSTA_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
/** Biçimli telefon: +90 5xx xxx xx xx, 0 (312) 123 45 67, 0532-123-45-67 … (bitişik 10–11 hane aşağıdaki kuralla gider). */
const TELEFON_RE = /(?:\+?90[\s.-]?|0[\s.-]?)?\(?[2-5]\d{2}\)?[\s.-]\d{3}[\s.-]?\d{2}[\s.-]?\d{2}(?!\d)/g;
/** 10 haneli VKN, 11 haneli TCKN ve bitişik telefon. */
const UZUN_SAYI_RE = /(^|[^\d])\d{10,11}(?=[^\d]|$)/g;

/** Metindeki kişisel/gizli veriyi maskeler. Hiç fırlatmaz. */
export function hataMetniMaskele(metin: unknown): string {
  try {
    if (metin === null || metin === undefined) {
      return '';
    }
    let s = typeof metin === 'string' ? metin : String(metin);
    s = s
      .replace(JWT_RE, MASKE)
      .replace(BEARER_RE, '$1 ' + MASKE)
      .replace(URL_PARAM_RE, '$1' + MASKE)
      .replace(JSON_ALAN_RE, '$1"' + MASKE + '"')
      .replace(EPOSTA_RE, MASKE)
      .replace(TELEFON_RE, MASKE)
      .replace(UZUN_SAYI_RE, '$1' + MASKE);
    return s.length > MESAJ_SINIR ? s.slice(0, MESAJ_SINIR) + '…' : s;
  } catch {
    return '';
  }
}

/**
 * Adresi maskeler. Önce %-kodlaması çözülür: OData süzgeci `filter=%5B%22Tc%22,%2212345678901%22%5D` gibi gelir,
 * çözülmezse TCKN `%22` ile bitişik kalıp sayı kuralından kaçardı.
 */
export function hataAdresiMaskele(url: unknown): string {
  try {
    if (url === null || url === undefined || url === '') {
      return '';
    }
    let s = String(url);
    try {
      s = decodeURIComponent(s.replace(/\+/g, ' '));
    } catch {
      // bozuk %-kodlaması: olduğu gibi maskele
    }
    return hataMetniMaskele(s);
  } catch {
    return '';
  }
}

/** HTML'i düz metne çevirir (toast mesajları <ul><li>/<br> içerebiliyor). */
function htmlDuzMetin(html: string): string {
  return html
    .replace(/<\s*br\s*\/?>/gi, ' | ')
    .replace(/<\/li>\s*<li[^>]*>/gi, ' | ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/** ServiceStack / DataServices cevap gövdesinden okunabilir mesajı çıkarır (UtilsService.extractServiceMessages'ın sade kopyası). */
function govdedenMesaj(govde: unknown, derinlik = 0): string[] {
  if (govde === null || govde === undefined || derinlik > 3) {
    return [];
  }
  if (typeof govde === 'string') {
    const t = govde.trim();
    if (!t) {
      return [];
    }
    if (t.startsWith('<')) {
      return ['(HTML hata sayfası)'];
    }
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        return govdedenMesaj(JSON.parse(t), derinlik + 1);
      } catch {
        // JSON değil, düz metin olarak al
      }
    }
    return [t];
  }
  if (typeof govde !== 'object') {
    return [String(govde)];
  }
  const o: any = govde;
  const msgs: string[] = [];
  const ekle = (v: any) => {
    const m = v && typeof v === 'object' ? v.message ?? v.Message ?? v.errorMessage ?? v.ErrorMessage : v;
    if (typeof m === 'string' && m.trim()) {
      msgs.push(m.trim());
    }
  };
  const listeEkle = (l: any) => {
    if (Array.isArray(l)) {
      l.forEach(ekle);
    }
  };
  const durum = o.responseStatus ?? o.ResponseStatus;
  if (durum) {
    msgs.push(...govdedenMesaj(durum, derinlik + 1));
  }
  if (o.Error && typeof o.Error === 'object') {
    ekle(o.Error.Message);
    listeEkle(o.Error.Details);
  } else if (o.error && typeof o.error === 'object') {
    msgs.push(...govdedenMesaj(o.error, derinlik + 1));
  }
  ekle(o.message);
  ekle(o.Message);
  ekle(o.errorMessage);
  ekle(o.ErrorMessage);
  listeEkle(o.errors);
  listeEkle(o.Errors);
  listeEkle(o.errorDetails);
  listeEkle(o.ErrorDetails);
  return [...new Set(msgs)];
}

function sayiMi(v: unknown): v is number {
  return typeof v === 'number' && isFinite(v);
}

export class EkranHataKaydiDeposu {
  private kayitlar: (EkranHataKaydi & { t: number })[] = [];
  private sayac = 0;
  private susturma = 0;
  /** dtokgoz 16.10: son tıklanan düğme (sekmeden bağımsız) ve sekme başına son istek. */
  private sonTiklama: { etiket: string; t: number } | null = null;
  private sonIstekler = new Map<string, { istek: string; t: number }>();
  /**
   * dtokgoz 17.10: kayıt kaynakları (footer bileşenleri). Kayıt hata anında `oku()` ile okunur (formda yerinde değişen
   * veri de güncel gelir); bileşen yok olunca `kayitKaynagiBirak` ile silinir. Kayıt nesnesi burada tutulmaz.
   */
  private kayitKaynaklari = new Map<object, { sekme: string; t: number; oku: () => { form?: unknown; kayit?: unknown } }>();

  /** Etkin sekme bir kayıt detayıysa başlığı (dabnet-kamu'da DkNavService kurar); değilse boş. */
  sekmeBasligiSaglayici: (() => string | null | undefined) | null = null;

  constructor() {
    this.tiklamaDinle();
  }

  /**
   * Etkin sekmeyi söyleyen işlev (dabnet-kamu'da DkNavService kurar). Yoksa ya da boş dönerse sayfa adresi kullanılır.
   * Hata kaydına ve footer'ın süzmesine aynı değer girer.
   */
  sekmeSaglayici: (() => string | null | undefined) | null = null;

  /** Şu anki ekran/sekme anahtarı. Hiç fırlatmaz. */
  aktifSekme(): string {
    try {
      const s = this.sekmeSaglayici ? this.sekmeSaglayici() : '';
      if (s) {
        return String(s);
      }
    } catch {
      // sağlayıcı bozuksa adrese düş
    }
    try {
      const l = typeof location !== 'undefined' ? location : null;
      return l ? (l.pathname || '') + (l.hash || '') : '';
    } catch {
      return '';
    }
  }

  /** HttpClient hatası (AuthInterceptor). Hiç fırlatmaz, girdiyi değiştirmez. */
  http(g: EkranHataHttpGirdi | null | undefined): void {
    try {
      if (this.susturma > 0 || !g) {
        return;
      }
      const durum = sayiMi(g.durum) ? g.durum : undefined;
      let mesaj = '';
      try {
        mesaj = govdedenMesaj(g.govde).join(' | ');
      } catch {
        mesaj = '';
      }
      if (!mesaj) {
        mesaj = durum === 0 ? 'Sunucuya ulaşılamadı' : (g.durumMetni || (durum !== undefined ? 'HTTP ' + durum : 'Hata'));
      }
      this.ekle({
        kaynak: 'http',
        yontem: g.yontem ? String(g.yontem).toUpperCase() : undefined,
        url: g.url ? hataAdresiMaskele(g.url) : undefined,
        durum,
        sureMs: sayiMi(g.sureMs) ? Math.max(0, Math.round(g.sureMs)) : undefined,
        mesaj: hataMetniMaskele(mesaj),
        islem: this.sonIslem(), // dtokgoz 16.10
      });
    } catch {
      // kayıt yazılamadıysa sessizce geç — hata akışı etkilenmez
    }
  }

  /**
   * AspNetData (OData) onAjaxError. `olay` nesnesine (özellikle `olay.error`) DOKUNMAZ: kütüphane handler'dan sonra
   * `error = e.error` okur, burada değiştirilirse ekranın gördüğü hata değişirdi.
   */
  odata(olay: { xhr?: any; error?: any } | null | undefined, varsayilanUrl?: string): void {
    try {
      if (this.susturma > 0 || !olay) {
        return;
      }
      const xhr = olay.xhr;
      let durum: number | undefined;
      let url = '';
      let govde: unknown;
      try {
        durum = sayiMi(xhr?.status) ? xhr.status : undefined;
        url = (xhr?.responseURL as string) || '';
        govde = xhr?.responseText;
      } catch {
        // xhr okunamadı (responseType farklı vb.)
      }
      let mesaj = '';
      try {
        mesaj = govdedenMesaj(govde).join(' | ');
        if (!mesaj) {
          const e = olay.error;
          mesaj = typeof e === 'string' ? e : e?.message ? String(e.message) : '';
          if (mesaj) {
            mesaj = govdedenMesaj(mesaj).join(' | ') || mesaj;
          }
        }
      } catch {
        mesaj = '';
      }
      if (!mesaj) {
        mesaj = durum === 0 ? 'Sunucuya ulaşılamadı' : durum !== undefined ? 'HTTP ' + durum : 'Hata';
      }
      this.ekle({
        kaynak: 'odata',
        url: hataAdresiMaskele(url || varsayilanUrl || ''),
        durum,
        mesaj: hataMetniMaskele(mesaj),
        islem: this.sonIslem(), // dtokgoz 16.10
      });
    } catch {
      // sessiz
    }
  }

  /**
   * Kullanıcıya gösterilen hata (UtilsService.showError). Son http/odata kaydı BIRLESTIRME_MS içinde ve aynı sekmede
   * oluşmuş, henüz gösterilmemişse ona eklenir (aynı hata iki satır olmasın); yoksa yeni 'ekran' kaydı açılır.
   */
  ekran(baslik: unknown, gosterilenHtml: unknown): void {
    try {
      if (this.susturma > 0) {
        return;
      }
      const metin = hataMetniMaskele(htmlDuzMetin(typeof gosterilenHtml === 'string' ? gosterilenHtml : String(gosterilenHtml ?? '')));
      const bas = hataMetniMaskele(typeof baslik === 'string' ? baslik : '');
      if (!metin && !bas) {
        return;
      }
      const sekme = this.aktifSekme();
      // dtokgoz 16.10: showError'ı çağıran metot (yığından)
      const cagiran = this.cagiranlar();
      const son = this.kayitlar[this.kayitlar.length - 1];
      if (son && son.kaynak !== 'ekran' && !son.gosterilen && Date.now() - son.t <= BIRLESTIRME_MS && son.sekme === sekme) {
        son.gosterilen = metin;
        son.baslik = bas;
        if (cagiran.length) {
          son.cagiran = cagiran;
        }
        return;
      }
      // dtokgoz 16.10: eski hâli — kaynak (düğme/istek/metot) yoktu
      // this.ekle({ kaynak: 'ekran', mesaj: metin || bas, baslik: bas, gosterilen: metin }, sekme);
      this.ekle(
        {
          kaynak: 'ekran',
          mesaj: metin || bas,
          baslik: bas,
          gosterilen: metin,
          islem: this.sonIslem(),
          sonIstek: this.sonIstek(sekme),
          cagiran: cagiran.length ? cagiran : undefined,
        },
        sekme
      );
    } catch {
      // sessiz
    }
  }

  /**
   * dtokgoz 16.10: başlayan her servis isteği (AuthInterceptor, DataCoreService.appendAuth). Yalnız yöntem + maskeli
   * adres, sekme başına sonuncusu tutulur; gövde/başlık yazılmaz. Hiç fırlatmaz.
   */
  istek(yontem: unknown, url: unknown): void {
    try {
      if (!url) {
        return;
      }
      const adres = hataAdresiMaskele(url);
      if (!adres) {
        return;
      }
      const y = yontem ? String(yontem).toUpperCase() : '';
      const sekme = this.aktifSekme();
      this.sonIstekler.delete(sekme);
      this.sonIstekler.set(sekme, { istek: (y ? y + ' ' : '') + adres, t: Date.now() });
      if (this.sonIstekler.size > KAPASITE) {
        const ilk = this.sonIstekler.keys().next().value;
        if (ilk !== undefined) {
          this.sonIstekler.delete(ilk);
        }
      }
    } catch {
      // sessiz
    }
  }

  /**
   * dtokgoz 17.10: footer "bu sekmede açık/seçili kayıt bende" der (ngOnChanges'te kayıt değişince). Aynı sekmede en son
   * bildiren kazanır: popup'taki detay formunun footer'ı açılınca öne geçer, kapanınca (bırakınca) liste footer'ına döner.
   * Hiç fırlatmaz.
   */
  kayitKaynagiBildir(sahip: object, oku: () => { form?: unknown; kayit?: unknown }): void {
    try {
      if (!sahip || typeof oku !== 'function') {
        return;
      }
      this.kayitKaynaklari.set(sahip, { sekme: this.aktifSekme(), t: Date.now(), oku });
    } catch {
      // sessiz
    }
  }

  /** Bileşen yok olurken (ngOnDestroy). Hiç fırlatmaz. */
  kayitKaynagiBirak(sahip: object): void {
    try {
      this.kayitKaynaklari.delete(sahip);
    } catch {
      // sessiz
    }
  }

  /**
   * dtokgoz 17.10: kayıttan tanıtıcı bilgi — Kod + KAYIT_ANAHTAR'daki ilk alanlar, yoksa adı "No" ile biten alanlar.
   * Kişisel/finansal alanlar (KAYIT_HARIC) hiç alınmaz, değerler maskelenir. Kod da anahtar da yoksa null. Hiç fırlatmaz.
   */
  static kayitOzeti(form: unknown, kayit: unknown): EkranHataKayitBilgisi | null {
    try {
      if (!kayit || typeof kayit !== 'object' || Array.isArray(kayit)) {
        return null;
      }
      const o: any = kayit;
      const deger = (v: unknown): string => {
        if (typeof v !== 'string' && typeof v !== 'number') {
          return '';
        }
        const s = hataMetniMaskele(String(v).trim());
        return s.length > KAYIT_DEGER_SINIR ? s.slice(0, KAYIT_DEGER_SINIR) + '…' : s;
      };
      const anahtarlar = Object.keys(o);
      const bul = (ad: string) => anahtarlar.find((a) => a.toLowerCase() === ad.toLowerCase());
      const alanlar: { [ad: string]: string } = {};
      let adet = 0;
      const ekle = (a: string | undefined) => {
        if (!a || adet >= KAYIT_ALAN_AZAMI || a in alanlar || KAYIT_HARIC.test(a)) {
          return;
        }
        const d = deger(o[a]);
        if (d) {
          alanlar[a] = d;
          adet++;
        }
      };
      KAYIT_ANAHTAR.forEach((a) => ekle(bul(a)));
      if (!adet) {
        anahtarlar.filter((a) => /.(No|Numarasi)$/.test(a)).forEach(ekle);
      }
      const kodAdi = bul('Kod');
      const kod = kodAdi ? deger(o[kodAdi]) : '';
      if (!kod && !adet) {
        return null;
      }
      const f = typeof form === 'string' ? form.slice(form.lastIndexOf('/') + 1).trim() : '';
      const sonuc: EkranHataKayitBilgisi = {};
      if (f) {
        sonuc.form = hataMetniMaskele(f).slice(0, 100);
      }
      if (kod) {
        sonuc.kod = kod;
      }
      if (adet) {
        sonuc.alanlar = alanlar;
      }
      return sonuc;
    } catch {
      return null;
    }
  }

  /** Sekmede en son bildirilmiş, kaydı dolu kaynak + detay sekmesi başlığı. */
  private acikKayit(sekme: string): EkranHataKayitBilgisi | undefined {
    try {
      const adaylar = [...this.kayitKaynaklari.values()].filter((v) => v.sekme === sekme).sort((a, b) => b.t - a.t);
      let sonuc: EkranHataKayitBilgisi | null = null;
      for (const a of adaylar) {
        try {
          const r = a.oku() || {};
          sonuc = EkranHataKaydiDeposu.kayitOzeti(r.form, r.kayit);
        } catch {
          sonuc = null;
        }
        if (sonuc) {
          break;
        }
      }
      let baslik = '';
      try {
        baslik = this.sekmeBasligiSaglayici ? String(this.sekmeBasligiSaglayici() || '') : '';
      } catch {
        baslik = '';
      }
      baslik = hataMetniMaskele(baslik.trim()).slice(0, 120);
      if (baslik) {
        sonuc = { ...(sonuc || {}), baslik };
      }
      return sonuc || undefined;
    } catch {
      return undefined;
    }
  }

  /** fn çalışırken kayıt yazılmaz (footer eski errorResponse'u yeniden gösterirken kopya satır açılmasın). fn'in sonucu/istisnası aynen geçer. */
  kaydetmeden<T>(fn: () => T): T {
    this.susturma++;
    try {
      return fn();
    } finally {
      this.susturma--;
    }
  }

  /**
   * En yeniden eskiye kayıtlar (kopya). `sekme` verilirse yalnız o sekmede oluşanlar. Hiç fırlatmaz.
   */
  liste(sekme?: string | null, adet: number = KAPASITE): EkranHataKaydi[] {
    try {
      const sonuc: EkranHataKaydi[] = [];
      for (let i = this.kayitlar.length - 1; i >= 0 && sonuc.length < adet; i--) {
        const k = this.kayitlar[i];
        if (sekme === undefined || sekme === null || k.sekme === sekme) {
          const { t, ...kopya } = k;
          sonuc.push({ ...kopya, zaman: new Date(k.zaman.getTime()) });
        }
      }
      return sonuc;
    } catch {
      return [];
    }
  }

  /** Footer/toast için güvenli (kaçışlı) HTML liste. */
  listeHtml(kayitlar: EkranHataKaydi[]): string {
    try {
      const kac = (v: string) =>
        v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const iki = (n: number) => (n < 10 ? '0' : '') + n;
      const satirlar = kayitlar.map((k) => {
        const z = k.zaman;
        const saat = iki(z.getHours()) + ':' + iki(z.getMinutes()) + ':' + iki(z.getSeconds());
        const adres = k.url ? EkranHataKaydiDeposu.kisaAdres(k.url) : '';
        const ust = [saat, k.durum !== undefined ? String(k.durum) : '', [k.yontem, adres].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(' · ');
        const metin = k.gosterilen || k.mesaj;
        return '<li><b>' + kac(ust) + '</b><br>' + kac(metin.length > 240 ? metin.slice(0, 240) + '…' : metin) + '</li>';
      });
      return '<ol style="margin:0;padding-left:18px">' + satirlar.join('') + '</ol>';
    } catch {
      return '';
    }
  }

  /** Testler için. */
  temizle(): void {
    this.kayitlar = [];
    this.sayac = 0;
    this.susturma = 0;
    this.sonTiklama = null; // dtokgoz 16.10
    this.sonIstekler.clear();
    this.kayitKaynaklari.clear(); // dtokgoz 17.10
  }

  /** dtokgoz 16.10: tıklanan öğenin etiketi (aria-label / title / görünen metin / id), maskeli ve kırpılmış. Hiç fırlatmaz. */
  static tiklamaEtiketi(hedef: unknown): string {
    try {
      const el =
        hedef && typeof (hedef as any).closest === 'function' ? ((hedef as Element).closest(TIKLAMA_SECICI) as HTMLElement | null) : null;
      if (!el || el.closest('#toast-container')) {
        return '';
      }
      const ham =
        el.getAttribute('aria-label') || el.getAttribute('title') || el.innerText || el.textContent || el.id || '';
      const metin = hataMetniMaskele(String(ham).replace(/\s+/g, ' ').trim());
      return metin.length > ETIKET_SINIR ? metin.slice(0, ETIKET_SINIR) + '…' : metin;
    } catch {
      return '';
    }
  }

  /**
   * dtokgoz 16.10: Error.stack'ten fonksiyon adları (Chrome "at A.b (…)", Firefox "b@…"). Adsız çerçeve (async devamı,
   * ok fonksiyonu) "@dosya:satır" olarak gelir. Hiç fırlatmaz.
   */
  static yigindanAdlar(yigin: unknown): string[] {
    try {
      if (typeof yigin !== 'string') {
        return [];
      }
      const adlar: string[] = [];
      for (const satir of yigin.split('\n')) {
        const t = satir.trim();
        let ad = '';
        const chrome = /^at\s+(?:async\s+)?(.+?)\s+\(/.exec(t);
        const adsiz = /^(?:at\s+(?:async\s+)?|@)(\S+?):(\d+):\d+\)?$/.exec(t);
        if (chrome) {
          ad = chrome[1];
        } else if (adsiz) {
          const dosya = adsiz[1].split('?')[0];
          ad = '@' + dosya.slice(dosya.lastIndexOf('/') + 1) + ':' + adsiz[2];
        } else if (!t.startsWith('at ')) {
          const firefox = /^([^@\s]+)@/.exec(t);
          ad = firefox ? firefox[1] : '';
        }
        ad = ad.replace(/\s*\[as [^\]]+\]$/, '').trim();
        if (ad) {
          adlar.push(ad);
        }
      }
      return adlar;
    } catch {
      return [];
    }
  }

  /**
   * showError'dan sonraki ilk anlamlı metotlar. Küçültülmüş (prod) sınıf adı ("t.kpsGuncelle") atılır, yalnız metot kalır.
   * Hiç adlı metot yoksa (servis yanıtını işleyen async/ok fonksiyonu — en sık durum) ilk adsız çerçevenin "@dosya:satır"ı.
   */
  private cagiranlar(): string[] {
    try {
      const E: any = Error;
      const eskiSinir = E.stackTraceLimit;
      let yigin: string | undefined;
      try {
        if (typeof eskiSinir === 'number') {
          E.stackTraceLimit = 40;
        }
        yigin = new Error().stack;
      } finally {
        if (typeof eskiSinir === 'number') {
          E.stackTraceLimit = eskiSinir;
        }
      }
      const adlar = EkranHataKaydiDeposu.yigindanAdlar(yigin);
      let bas = -1;
      adlar.forEach((a, i) => {
        if (/(^|\.)showError$/.test(a)) {
          bas = i;
        }
      });
      const sonuc: string[] = [];
      let ilkAdsiz = '';
      for (let i = bas + 1; i < adlar.length && sonuc.length < YIGIN_AZAMI; i++) {
        let ad = adlar[i];
        if (ad.startsWith('@')) {
          if (!ilkAdsiz && !/^@(polyfills|vendor|runtime|zone)/.test(ad)) {
            ilkAdsiz = ad;
          }
          continue;
        }
        if (ad.startsWith('EkranHataKaydiDeposu.') || /(^|\.)(ekran|cagiranlar|showError)$/.test(ad)) {
          continue;
        }
        const nokta = ad.lastIndexOf('.');
        if (nokta > 0 && nokta <= 2) {
          ad = ad.slice(nokta + 1); // "t.kpsGuncelle" → "kpsGuncelle"
        }
        if (!ad || ad.length < 3 || YIGIN_ATLA.test(ad) || sonuc.includes(ad)) {
          continue;
        }
        sonuc.push(ad.length > ETIKET_SINIR ? ad.slice(0, ETIKET_SINIR) : ad);
      }
      if (!sonuc.length && ilkAdsiz) {
        sonuc.push(ilkAdsiz.slice(0, ETIKET_SINIR));
      }
      return sonuc;
    } catch {
      return [];
    }
  }

  private sonIslem(): string | undefined {
    const s = this.sonTiklama;
    return s && Date.now() - s.t <= ISLEM_MS ? s.etiket : undefined;
  }

  private sonIstek(sekme: string): string | undefined {
    const s = this.sonIstekler.get(sekme);
    return s && Date.now() - s.t <= ISTEK_MS ? s.istek : undefined;
  }

  /** Belge düzeyinde (capture) tıklama dinleyicisi — olayı yalnız okur, durdurmaz/değiştirmez. Tampon tek olduğu için bir kez kurulur. */
  private tiklamaDinle(): void {
    try {
      if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
        return;
      }
      document.addEventListener(
        'click',
        (olay) => {
          try {
            const etiket = EkranHataKaydiDeposu.tiklamaEtiketi(olay.target);
            if (etiket) {
              this.sonTiklama = { etiket, t: Date.now() };
            }
          } catch {
            // sessiz
          }
        },
        { capture: true, passive: true }
      );
    } catch {
      // sessiz
    }
  }

  /** 'https://x/gysservices/api/GysBeyanGetir?..' → 'gysservices/…/GysBeyanGetir' (sorgu dizesi gösterilmez). */
  static kisaAdres(url: string): string {
    try {
      const yol = url.split('?')[0].replace(/^[a-z]+:\/\/[^/]+/i, '');
      const parca = yol.split('/').filter(Boolean);
      if (parca.length <= 2) {
        return parca.join('/');
      }
      return parca[0] + '/…/' + parca[parca.length - 1];
    } catch {
      return '';
    }
  }

  private ekle(k: Omit<EkranHataKaydi, 'no' | 'zaman' | 'sekme'>, sekme?: string): void {
    const t = Date.now();
    // dtokgoz 17.10: eski hâli — açık kayıt bilgisi yoktu
    // this.kayitlar.push({ ...k, no: ++this.sayac, zaman: new Date(t), sekme: sekme ?? this.aktifSekme(), t });
    const s = sekme ?? this.aktifSekme();
    const kayit = this.acikKayit(s);
    this.kayitlar.push({ ...k, ...(kayit ? { kayit } : {}), no: ++this.sayac, zaman: new Date(t), sekme: s, t });
    if (this.kayitlar.length > KAPASITE) {
      this.kayitlar.splice(0, this.kayitlar.length - KAPASITE);
    }
  }
}

/**
 * Uygulama genelinde tek tampon (DI'sız; interceptor, servisler ve footer'lar aynı nesneyi kullanır).
 * globalThis'e bağlanır: dosya farklı yol adlarıyla (göreli / @dabnet-kamu/libs/ui/... takma adı) iki kez yüklense de
 * (Jest modül kaydı böyle yapıyor; tembel parçalarda da olabilir) tek nesne kalır.
 */
export const ekranHataKaydi: EkranHataKaydiDeposu =
  ((globalThis as any).__dkEkranHataKaydi as EkranHataKaydiDeposu) ||
  ((globalThis as any).__dkEkranHataKaydi = new EkranHataKaydiDeposu());
